import { normalizeCurrencyValue } from '@/lib/utils'
import type { NumericInput } from '@/lib/utils'

/** The payment fields that decide whether POS still synchronizes a card payment (PosCartResource `payments`). */
interface CardPaymentCandidate {
  type?: string | null
  status?: string | null
  amount_applied?: NumericInput
  source_payment_id?: number | null
}

interface FinalizingCart<Payment extends CardPaymentCandidate> {
  active_order_placement_attempt_id?: number | null
  payments?: readonly (Payment | null | undefined)[] | null
}

// The API field still happens to be named `active_order_placement_attempt_id`,
// but in POS we treat it as the explicit final-settlement contract selector.
export function getCartFinalizationAttemptId(
  cart:
    | Pick<FinalizingCart<CardPaymentCandidate>, 'active_order_placement_attempt_id'>
    | null
    | undefined
): number | null {
  const attemptId = Number(cart?.active_order_placement_attempt_id ?? 0)

  return attemptId > 0 ? attemptId : null
}

export function isSynchronizablePendingPosCardPayment(
  payment: CardPaymentCandidate | null | undefined
): payment is CardPaymentCandidate {
  if (!payment) {
    return false
  }

  const appliedAmount = normalizeCurrencyValue(payment.amount_applied ?? 0)
  const status = String(payment.status ?? '')
    .toLowerCase()
    .trim()

  return (
    String(payment.type ?? '') === 'card' &&
    (payment.source_payment_id ?? null) === null &&
    appliedAmount > 0 &&
    ['pending', 'processing'].includes(status)
  )
}

export function getPosPendingCardPayments<Payment extends CardPaymentCandidate>(
  cart: FinalizingCart<Payment> | null | undefined
): Payment[] {
  if (getCartFinalizationAttemptId(cart)) {
    return []
  }

  return (cart?.payments ?? []).filter((payment): payment is Payment =>
    isSynchronizablePendingPosCardPayment(payment)
  )
}
